/**
 * Zoom math for the contract preview: an A4 sheet (21cm) plus a 16px gutter
 * on each side, scaled with a CSS transform. `'fit'` follows the column
 * width; a number is a fixed scale the viewer picked.
 */

export type PreviewZoom = 'fit' | number;

export const A4_WIDTH_PX = (21 / 2.54) * 96;
export const SHEET_GUTTER_PX = 16;
export const SHEET_WIDTH_PX = A4_WIDTH_PX + 2 * SHEET_GUTTER_PX;

export const MIN_ZOOM = 0.3;
export const MAX_ZOOM = 2;
/** Fit-to-width never blows the page up past this on very wide columns. */
export const MAX_FIT_ZOOM = 1;
export const ZOOM_STEPS: ReadonlyArray<number> = [
  0.3, 0.4, 0.5, 0.6, 0.67, 0.75, 0.8, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2,
];

export const ZOOM_STORAGE_KEY = 'regta.preview-zoom';

const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));

/** Scale at which the whole sheet width fits `availablePx`. */
export function fitScale(availablePx: number, sheetPx: number = SHEET_WIDTH_PX): number {
  if (!Number.isFinite(availablePx) || availablePx <= 0 || sheetPx <= 0) return 1;
  return clamp(Math.floor((availablePx / sheetPx) * 1000) / 1000, MIN_ZOOM, MAX_FIT_ZOOM);
}

export function resolveScale(zoom: PreviewZoom, fit: number): number {
  return zoom === 'fit' ? fit : clamp(zoom, MIN_ZOOM, MAX_ZOOM);
}

/** Next step above (`+1`) or below (`-1`) the scale currently shown. */
export function stepZoom(current: number, dir: 1 | -1): number {
  const eps = 0.005;
  if (dir > 0) return ZOOM_STEPS.find((s) => s > current + eps) ?? MAX_ZOOM;
  return [...ZOOM_STEPS].reverse().find((s) => s < current - eps) ?? MIN_ZOOM;
}

export function canStep(current: number, dir: 1 | -1): boolean {
  return dir > 0 ? current < MAX_ZOOM - 0.005 : current > MIN_ZOOM + 0.005;
}

export function formatZoom(scale: number): string {
  return `${Math.round(scale * 100)} %`;
}

export function parseZoom(raw: string | null | undefined): PreviewZoom {
  if (!raw || raw === 'fit') return 'fit';
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? clamp(n, MIN_ZOOM, MAX_ZOOM) : 'fit';
}

export function serializeZoom(zoom: PreviewZoom): string {
  return zoom === 'fit' ? 'fit' : String(zoom);
}

/**
 * Layout for a transform-scaled sheet inside a box `availablePx` wide: lay
 * the content out `layoutWidth` wide (so it centres as at 100 %), then
 * scale it to exactly the box width — or wider when zoomed past fit, which
 * the box scrolls horizontally.
 */
export function scaledLayout(
  availablePx: number,
  scale: number,
  sheetPx: number = SHEET_WIDTH_PX,
): { layoutWidth: number; visualWidth: number } {
  const layoutWidth = Math.max(sheetPx, availablePx / scale);
  return { layoutWidth, visualWidth: layoutWidth * scale };
}
